import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { auditApi, type AuditEntry } from '@/lib/api';
import { Table, Thead, Tbody, Th, Td, Tr } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/input';
import { Card, CardHeader, CardBody } from '@/components/ui/card';

function fmt(dt: string) {
  return new Date(dt).toLocaleString();
}

export function AuditPage() {
  const [page, setPage] = useState(1);
  const [entityType, setEntityType] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['audit', page, entityType],
    queryFn: () => auditApi.list(page, entityType || undefined),
  });

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader
          title={`Audit Log (${data?.pagination.total ?? 0})`}
          action={
            <Select
              value={entityType}
              onChange={e => { setEntityType(e.target.value); setPage(1); }}
              className="py-1 text-xs"
            >
              <option value="">All types</option>
              <option value="equipment">Equipment</option>
              <option value="part">Part</option>
              <option value="maintenance_task">Maintenance Task</option>
              <option value="work_order">Work Order</option>
              <option value="user">User</option>
            </Select>
          }
        />
        <CardBody className="p-0">
          {isLoading ? (
            <p className="px-5 py-8 text-center text-slate-400">Loading…</p>
          ) : (
            <Table>
              <Thead>
                <tr>
                  <Th>Time</Th><Th>User</Th><Th>Action</Th>
                  <Th>Entity Type</Th><Th>Entity ID</Th><Th>Details</Th>
                </tr>
              </Thead>
              <Tbody>
                {data?.data.map((e: AuditEntry) => (
                  <Tr key={e.id}>
                    <Td className="text-xs whitespace-nowrap">{fmt(e.created_at)}</Td>
                    <Td className="text-xs">{e.user?.full_name ?? '—'}</Td>
                    <Td className="font-mono text-xs">{e.action}</Td>
                    <Td className="text-xs">{e.entity_type ?? '—'}</Td>
                    <Td className="font-mono text-xs truncate max-w-[8rem]">{e.entity_id ?? '—'}</Td>
                    <Td className="text-xs text-slate-500">
                      {e.details ? JSON.stringify(e.details).slice(0, 80) : '—'}
                    </Td>
                  </Tr>
                ))}
                {data?.data.length === 0 && (
                  <Tr><Td className="py-8 text-center text-slate-400" colSpan={6}>No audit entries found.</Td></Tr>
                )}
              </Tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {(data?.pagination.total ?? 0) > 50 && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>Prev</Button>
          <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page * 50 >= (data?.pagination.total ?? 0)}>Next</Button>
        </div>
      )}
    </div>
  );
}
